/**
 * In-memory mock layer for SubcontractorInvoices UI testing.
 *
 * Activate by appending ?mock=1 to either:
 *   /hmi/subcontractor-invoices        (admin UI)
 *   /subcontractor/invoices            (sub portal)
 *
 * What it does:
 *   - Replaces all 4 axios calls (list, detail, approve/pay/void, adjust) with
 *     pure in-memory functions backed by the FIXTURES array below.
 *   - Mutations update the array, so clicking Approve / Mark Paid / Void / Adjust
 *     does what you'd expect (status flips, finalAmount recomputes). Refreshing
 *     the page resets to initial fixtures.
 *
 * Why it exists:
 *   The user runs the dev stack in Docker, so seed scripts that connect to mongo
 *   are inconvenient. This lets them verify UI layout + status-gating + interactions
 *   directly in the browser without touching the database.
 */

export const isMockMode = () => {
  if (typeof window === 'undefined') return false
  try {
    const params = new URLSearchParams(window.location.search)
    return params.get('mock') === '1'
  } catch {
    return false
  }
}

const FAKE_PDF_SUB = 'https://example.com/test-sub-spray-log.pdf'
const FAKE_PDF_OUR = 'https://example.com/test-our-spray-log.pdf'

const now = Date.now()
const dayMs = 24 * 60 * 60 * 1000

const cloneDate = (offsetDays) => new Date(now - offsetDays * dayMs).toISOString()

// Initial fixture state — five invoices covering the full status matrix.
// Note: real backend returns _id as a string of an ObjectId; we use plain strings here.
const initialFixtures = () => [
  {
    _id: 'mock-invoice-001',
    invoiceNumber: 'SUB-TEST-00001',
    status: 'draft',
    ratePerAcre: 350,
    acreage: 3,
    amount: 1050,
    finalAmount: 1050,
    adjustments: [],
    vendor: {
      _id: 'mock-vendor-001',
      name: 'Desert Greens Spray LLC',
      applicatorLicense: 'AZ-12345',
      pmdNumber: 'PMD-9001',
      qualifyingParty: 'Jane Applicator',
      portalBranding: {
        displayName: 'Desert Greens Spray',
        primaryColor: '#3f7a5a',
        logoUrl: 'https://placehold.co/240x80/3f7a5a/ffffff/png?text=Desert+Greens'
      }
    },
    entity: { _id: 'mock-entity-001', name: 'HMI Commercial Landscaping', slugName: 'hmi-commercial', physicalAddress: '1234 Desert Way, Phoenix AZ' },
    scheduleItem: { _id: 'mock-sched-001', siteName: 'Tatum Ranch HOA', siteAddress: '5400 E Tatum Blvd, Phoenix AZ', jobsite: 'mock-js-001' },
    sprayOperation: { _id: 'mock-op-001' },
    subSprayLogPdfUrl: FAKE_PDF_SUB,
    ourSprayLogPdfUrl: FAKE_PDF_OUR,
    created_at: cloneDate(0)
  },
  {
    _id: 'mock-invoice-002',
    invoiceNumber: 'SUB-TEST-00002',
    status: 'approved',
    ratePerAcre: 350,
    acreage: 5,
    amount: 1750,
    finalAmount: 1750,
    adjustments: [],
    vendor: {
      _id: 'mock-vendor-001',
      name: 'Desert Greens Spray LLC',
      applicatorLicense: 'AZ-12345',
      pmdNumber: 'PMD-9001',
      qualifyingParty: 'Jane Applicator',
      portalBranding: {
        displayName: 'Desert Greens Spray',
        primaryColor: '#3f7a5a',
        logoUrl: 'https://placehold.co/240x80/3f7a5a/ffffff/png?text=Desert+Greens'
      }
    },
    entity: { _id: 'mock-entity-002', name: 'Sun Ridge Properties', slugName: 'sun-ridge', physicalAddress: '5678 Sun Ridge Pkwy, Scottsdale AZ' },
    scheduleItem: { _id: 'mock-sched-002', siteName: 'Sun Ridge Apartments', siteAddress: '5678 Sun Ridge Pkwy, Scottsdale AZ', jobsite: 'mock-js-002' },
    sprayOperation: { _id: 'mock-op-002' },
    approvedAt: cloneDate(1),
    approvedBy: { _id: 'mock-emp-001', firstName: 'Chad', lastName: 'Reviewer' },
    subSprayLogPdfUrl: FAKE_PDF_SUB,
    ourSprayLogPdfUrl: FAKE_PDF_OUR,
    created_at: cloneDate(2)
  },
  {
    _id: 'mock-invoice-003',
    invoiceNumber: 'SUB-TEST-00003',
    status: 'paid',
    ratePerAcre: 350,
    acreage: 2.5,
    amount: 875,
    finalAmount: 875,
    adjustments: [],
    vendor: {
      _id: 'mock-vendor-002',
      name: 'Cactus Country Applicators',
      applicatorLicense: 'AZ-67890',
      pmdNumber: 'PMD-8745',
      qualifyingParty: 'Bob Sprayer',
      portalBranding: {
        displayName: 'Cactus Country Applicators',
        primaryColor: '#b25a2a',
        logoUrl: 'https://placehold.co/240x80/b25a2a/ffffff/png?text=Cactus+Country'
      }
    },
    entity: { _id: 'mock-entity-003', name: 'Pinnacle Peak Resort', slugName: 'pinnacle-peak', physicalAddress: '99 Pinnacle Peak Rd, Scottsdale AZ' },
    scheduleItem: { _id: 'mock-sched-003', siteName: 'Pinnacle Peak Golf Course', siteAddress: '99 Pinnacle Peak Rd, Scottsdale AZ', jobsite: 'mock-js-003' },
    sprayOperation: { _id: 'mock-op-003' },
    approvedAt: cloneDate(4),
    approvedBy: { _id: 'mock-emp-001', firstName: 'Chad', lastName: 'Reviewer' },
    paidAt: cloneDate(1),
    subSprayLogPdfUrl: FAKE_PDF_SUB,
    ourSprayLogPdfUrl: FAKE_PDF_OUR,
    created_at: cloneDate(5)
  },
  {
    _id: 'mock-invoice-004',
    invoiceNumber: 'SUB-TEST-00004',
    status: 'voided',
    ratePerAcre: 350,
    acreage: 1.5,
    amount: 525,
    finalAmount: 525,
    adjustments: [],
    vendor: {
      _id: 'mock-vendor-001',
      name: 'Desert Greens Spray LLC',
      applicatorLicense: 'AZ-12345',
      pmdNumber: 'PMD-9001',
      qualifyingParty: 'Jane Applicator',
      portalBranding: {
        displayName: 'Desert Greens Spray',
        primaryColor: '#3f7a5a',
        logoUrl: 'https://placehold.co/240x80/3f7a5a/ffffff/png?text=Desert+Greens'
      }
    },
    entity: { _id: 'mock-entity-001', name: 'HMI Commercial Landscaping', slugName: 'hmi-commercial', physicalAddress: '1234 Desert Way, Phoenix AZ' },
    scheduleItem: { _id: 'mock-sched-004', siteName: 'Cancelled — Test Site', siteAddress: '12 Test Loop, Mesa AZ', jobsite: 'mock-js-004' },
    sprayOperation: { _id: 'mock-op-004' },
    subSprayLogPdfUrl: FAKE_PDF_SUB,
    ourSprayLogPdfUrl: FAKE_PDF_OUR,
    created_at: cloneDate(7)
  },
  {
    _id: 'mock-invoice-005',
    invoiceNumber: 'SUB-TEST-00005',
    status: 'approved',
    ratePerAcre: 400,
    acreage: 6,
    amount: 2400,
    finalAmount: 2425, // 2400 - 50 + 75
    adjustments: [
      {
        description: 'Chemical credit (residual product returned)',
        amount: -50,
        adjustedBy: { _id: 'mock-emp-001', firstName: 'Chad', lastName: 'Reviewer' },
        adjustedAt: cloneDate(1)
      },
      {
        description: 'Extra labor for re-spray of zone 3',
        amount: 75,
        adjustedBy: { _id: 'mock-emp-001', firstName: 'Chad', lastName: 'Reviewer' },
        adjustedAt: cloneDate(0.5)
      }
    ],
    vendor: {
      _id: 'mock-vendor-002',
      name: 'Cactus Country Applicators',
      applicatorLicense: 'AZ-67890',
      pmdNumber: 'PMD-8745',
      qualifyingParty: 'Bob Sprayer',
      portalBranding: {
        displayName: 'Cactus Country Applicators',
        primaryColor: '#b25a2a',
        logoUrl: 'https://placehold.co/240x80/b25a2a/ffffff/png?text=Cactus+Country'
      }
    },
    entity: { _id: 'mock-entity-004', name: 'Camelback Crossing', slugName: 'camelback-crossing', physicalAddress: '4400 E Camelback Rd, Phoenix AZ' },
    scheduleItem: { _id: 'mock-sched-005', siteName: 'Camelback Crossing Common Area', siteAddress: '4400 E Camelback Rd, Phoenix AZ', jobsite: 'mock-js-005' },
    sprayOperation: { _id: 'mock-op-005' },
    approvedAt: cloneDate(1),
    approvedBy: { _id: 'mock-emp-001', firstName: 'Chad', lastName: 'Reviewer' },
    subSprayLogPdfUrl: FAKE_PDF_SUB,
    ourSprayLogPdfUrl: FAKE_PDF_OUR,
    created_at: cloneDate(3)
  }
]

// In-memory store — survives navigation within the SPA, resets on full page reload
let store = initialFixtures()

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms))

const recomputeFinalAmount = (invoice) => {
  const adjustmentTotal = (invoice.adjustments || []).reduce((sum, adj) => sum + (Number(adj.amount) || 0), 0)
  invoice.finalAmount = Math.round((invoice.amount + adjustmentTotal) * 100) / 100
}

// ── List GET /api/subcontractor-invoices ─────────────────────────────────────
export const mockList = async ({ status, vendor, dateStart, dateEnd, page = 1, limit = 50 } = {}) => {
  await sleep(80) // tiny delay so loading states render once
  let invoices = store.slice()
  if (status) invoices = invoices.filter(inv => inv.status === status)
  if (vendor) invoices = invoices.filter(inv => inv.vendor?._id === vendor || inv.vendor?.name === vendor)
  if (dateStart) {
    const d = new Date(dateStart)
    if (!isNaN(d.getTime())) invoices = invoices.filter(inv => new Date(inv.created_at) >= d)
  }
  if (dateEnd) {
    const d = new Date(dateEnd)
    if (!isNaN(d.getTime())) {
      d.setHours(23, 59, 59, 999)
      invoices = invoices.filter(inv => new Date(inv.created_at) <= d)
    }
  }
  // Newest first to match the real endpoint
  invoices.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
  const total = invoices.length
  const skip = (Number(page) - 1) * Number(limit)
  const paged = invoices.slice(skip, skip + Number(limit))
  return { invoices: paged, total, page: Number(page), limit: Number(limit) }
}

// ── Detail GET /api/subcontractor-invoices/:id ───────────────────────────────
export const mockGet = async (id) => {
  await sleep(60)
  const found = store.find(inv => inv._id === id)
  if (!found) {
    const err = new Error('Invoice not found')
    err.response = { status: 404, data: { error: 'not_found', message: 'Invoice not found' } }
    throw err
  }
  // Deep clone so callers can't mutate the store directly
  return JSON.parse(JSON.stringify(found))
}

// ── PUT /api/subcontractor-invoices/:id/approve ──────────────────────────────
export const mockApprove = async (id) => {
  await sleep(80)
  const invoice = store.find(inv => inv._id === id)
  if (!invoice) throwResponse(404, 'not_found', 'Invoice not found')
  if (invoice.status === 'voided') throwResponse(400, 'invalid_state', 'Cannot approve a voided invoice')
  if (invoice.status === 'paid') throwResponse(400, 'invalid_state', 'Cannot approve an already-paid invoice')
  invoice.status = 'approved'
  invoice.approvedAt = new Date().toISOString()
  invoice.approvedBy = { _id: 'mock-emp-001', firstName: 'Chad', lastName: 'Reviewer' }
  return { success: true, invoice: JSON.parse(JSON.stringify(invoice)) }
}

// ── PUT /api/subcontractor-invoices/:id/pay ──────────────────────────────────
export const mockPay = async (id) => {
  await sleep(80)
  const invoice = store.find(inv => inv._id === id)
  if (!invoice) throwResponse(404, 'not_found', 'Invoice not found')
  if (invoice.status !== 'approved') throwResponse(400, 'invalid_state', 'Invoice must be approved before marking as paid')
  invoice.status = 'paid'
  invoice.paidAt = new Date().toISOString()
  return { success: true, invoice: JSON.parse(JSON.stringify(invoice)) }
}

// ── PUT /api/subcontractor-invoices/:id/void ─────────────────────────────────
export const mockVoid = async (id) => {
  await sleep(80)
  const invoice = store.find(inv => inv._id === id)
  if (!invoice) throwResponse(404, 'not_found', 'Invoice not found')
  if (invoice.status === 'paid') throwResponse(400, 'invalid_state', 'Cannot void a paid invoice')
  invoice.status = 'voided'
  return { success: true, invoice: JSON.parse(JSON.stringify(invoice)) }
}

// ── PUT /api/subcontractor-invoices/:id/adjust ───────────────────────────────
export const mockAdjust = async (id, { description, amount }) => {
  await sleep(80)
  const invoice = store.find(inv => inv._id === id)
  if (!invoice) throwResponse(404, 'not_found', 'Invoice not found')
  if (invoice.status === 'voided') throwResponse(400, 'invalid_state', 'Cannot adjust a voided invoice')
  if (invoice.status === 'paid') throwResponse(400, 'invalid_state', 'Cannot adjust a paid invoice')
  if (!description || amount == null) throwResponse(400, 'validation', 'description and amount are required')
  invoice.adjustments = invoice.adjustments || []
  invoice.adjustments.push({
    description,
    amount: Number(amount),
    adjustedBy: { _id: 'mock-emp-001', firstName: 'Chad', lastName: 'Reviewer' },
    adjustedAt: new Date().toISOString()
  })
  recomputeFinalAmount(invoice)
  return { success: true, invoice: JSON.parse(JSON.stringify(invoice)) }
}

// ── POST /api/subcontractor-invoices/:id/create-aspire-receipt ───────────────
export const mockCreateAspireReceipt = async (id) => {
  await sleep(300)
  const invoice = store.find(inv => inv._id === id)
  if (!invoice) throwResponse(404, 'not_found', 'Invoice not found')
  if (invoice.aspireReceipt?.receiptId) throwResponse(409, 'aspire_receipt_failed', `Aspire receipt ${invoice.aspireReceipt.receiptId} already exists for this invoice`)
  if (!['approved', 'paid'].includes(invoice.status)) throwResponse(400, 'aspire_receipt_failed', `Invoice must be approved before creating an Aspire receipt (status: ${invoice.status})`)
  invoice.aspireReceipt = {
    receiptId: 90000 + Math.floor(Math.random() * 9999),
    createdAt: new Date().toISOString(),
    error: null
  }
  return { success: true, receiptId: invoice.aspireReceipt.receiptId, invoice: JSON.parse(JSON.stringify(invoice)) }
}

const throwResponse = (status, code, message) => {
  const err = new Error(message)
  err.response = { status, data: { error: code, message } }
  throw err
}

// Reset to initial fixtures (used by sub-portal page, since it shares the same store)
export const mockReset = () => {
  store = initialFixtures()
}
